import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Copy, CheckCircle, FileText } from "lucide-react";

const ExampleBrief = ({ isOpen, onClose, onUseExample }) => {
  const [copied, setCopied] = React.useState(false);

  const exampleText = `We're preparing to reintroduce our premium bottled water brand, GlacierDrop, after a three-year hiatus. The relaunch is timed with the opening of our new eco-conscious bottling facility in Montana. While the core branding will remain, we want the campaign to emphasize sustainability, community, and innovation. We're envisioning a series of short-form content pieces for digital and TV: 15-second ads for pre-roll and social media, a 30-second hero spot for national TV, and some behind-the-scenes footage that tells our brand story — who we are, what we believe in, and how this new facility represents our commitment to a better planet. We have some legacy assets, including logo, typography, and color palette, but we're open to refreshing the creative style. Timeline-wise, we'd like to start pre-production by mid-Q3, with launch expected in early Q4. We haven't locked the budget yet, but expect to allocate somewhere between $75k and $100k depending on concept complexity and media placements. We also want to collaborate with a team that can help us refine our message and tone. Prior experience in the beverage industry is a big plus. This project is critical to our 2026 brand strategy, so high production quality and narrative clarity are essential.`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(exampleText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy text: ", err);
    }
  };

  const handleUseExample = () => {
    onUseExample(exampleText);
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
          />
          
          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: "spring", duration: 0.3 }}
            className="relative w-full max-w-2xl max-h-[90vh] bg-white rounded-2xl overflow-hidden"
            style={{
              boxShadow: "0px 8px 32px rgba(16, 185, 129, 0.15)",
              border: "1px solid rgba(16, 185, 129, 0.1)",
            }}
          >
            {/* Header */}
            <div 
              className="flex items-center justify-between p-5 border-b"
              style={{
                background: "linear-gradient(135deg, #F0FDF4 0%, #ECFDF5 100%)",
                borderColor: "rgba(16, 185, 129, 0.15)",
              }}
            >
              <div className="flex items-center gap-3">
                <div 
                  className="w-10 h-10 rounded-full flex items-center justify-center"
                  style={{ background: "rgba(16, 185, 129, 0.15)" }}
                >
                  <FileText strokeWidth={2} size={20} style={{ color: "#10B981" }} />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Example Brief</h2>
                  <p className="text-sm text-gray-500">Sample campaign brief for reference</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="w-8 h-8 flex items-center justify-center rounded-full bg-white hover:bg-gray-100 transition-colors duration-200"
                style={{
                  boxShadow: "0px 1px 3px rgba(16, 24, 40, 0.08)",
                }}
              >
                <X className="w-4 h-4 text-gray-500" />
              </button>
            </div>

            {/* Content */}
            <div 
              className="p-5 overflow-y-auto max-h-[55vh]"
              style={{
                scrollbarWidth: "thin",
                scrollbarColor: "#e5e7eb transparent",
              }}
            >
              <div className="relative">
                <div className="absolute top-3 right-3 flex gap-2">
                  <button
                    onClick={handleCopy}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full transition-all duration-200"
                    style={{
                      background: copied ? "#F0FDF4" : "#F3F4F6",
                      color: copied ? "#10B981" : "#6B7280",
                      border: copied ? "1px solid #10B981" : "1px solid #E5E7EB",
                    }}
                  >
                    {copied ? (
                      <>
                        <CheckCircle className="w-3 h-3" />
                        <span>Copied!</span>
                      </>
                    ) : (
                      <>
                        <Copy className="w-3 h-3" />
                        Copy
                      </>
                    )}
                  </button>
                </div>
                
                <div 
                  className="rounded-xl p-5 pr-20"
                  style={{
                    background: "#FAFAFA",
                    border: "1px solid #E5E7EB",
                  }}
                >
                  <p className="text-gray-700 leading-relaxed text-sm whitespace-pre-wrap">
                    {exampleText}
                  </p>
                </div>
              </div>

              {/* Stats */}
              <div className="mt-4 flex items-center gap-4 text-xs text-gray-500">
                <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gray-100">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  {exampleText.length} characters
                </span>
                <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gray-100">
                  <div className="w-2 h-2 bg-teal-500 rounded-full"></div>
                  {exampleText.split(' ').length} words
                </span>
              </div>
            </div>

            {/* Footer */}
            <div 
              className="flex items-center justify-end gap-3 p-5 border-t"
              style={{ borderColor: "#F3F4F6" }}
            >
              <button
                onClick={onClose}
                className="flex h-[40px] px-5 items-center gap-2 rounded-full text-sm font-medium cursor-pointer transition-all duration-200 bg-gray-50 text-gray-600 hover:bg-gray-100 border-none"
                style={{
                  boxShadow: "0px 1px 3px 0px rgba(16, 24, 40, 0.08)",
                }}
              >
                Close
              </button>
              <button
                onClick={handleUseExample}
                className="flex h-[40px] px-5 items-center gap-2 rounded-full text-sm font-semibold cursor-pointer border-none text-white transition-all duration-200 hover:opacity-90"
                style={{
                  background: "linear-gradient(135deg, #10B981 0%, #059669 100%)",
                  boxShadow: "0px 2px 6px 0px rgba(16, 185, 129, 0.25)",
                }}
              >
                Use This Example
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default ExampleBrief;